<template>
  <div
    class="sitemap_page_row"
    :class="[
      `review-${page.review_status}`,
      {
        'sitemap_page_row_onboarding': isOnboarding,
      }
    ]"
    @mouseenter="hover = true"
    @mouseleave="hover = false"
  >
    <div class="sitemap_page">
      <div
        v-if="hover && pageCount > 1 && !isOnboarding || isEditing && pageCount > 1"
        class="ordering_buttons"
      >
        <div
          :class="[
            'order',
            pageIndex === 0 || disableReorder ? 'disabled' : '',
          ]"
          @click="reorderSitemapPage({pageIndex, direction: 'up'})"
        >
          <svg-vue
            class="move_up"
            icon="icons/caret-circle"
          />
        </div>
        <div
          :class="[
            'order',
            pageIndex === pageCount - 1 || disableReorder ? 'disabled' : '',
          ]"
          @click="reorderSitemapPage({pageIndex, direction: 'down'})"
        >
          <svg-vue
            class="move_down"
            icon="icons/caret-circle"
          />
        </div>
      </div>
      <div class="sitemap_page_info">
        <div class="page_title">
          <a
            :href="page.url"
            target="_blank"
          >
            {{ page.url }}
          </a>
          <svg-vue
            v-if="page.new"
            v-tooltip="{
              content: 'We recommend creating this new page on the website to accommodate these keywords. If you’re unsure how to proceed, please don’t hesitate to reach out.',
              placement: 'bottom',
              theme: 'dark-blue-tooltip',
            }"
            icon="icons/new"
            class="tw-w-6 tw-h-6 tw-cursor-pointer"
          />
          <svg-vue
            v-if="page.refreshing"
            class="fa-spin refreshing tooltipster"
            icon="icons/arrows-counter-clockwise"
            title="We are currently refreshing this page's data"
          />
          <svg-vue
            v-if="page.refreshError"
            class="refresh_error tooltipster"
            icon="icons/info-circle"
            title="We had trouble fetching this page's data"
          />
          <div
            v-if="!isOnboarding"
            class="page_url"
          >
            <svg-vue
              v-if="isEditing || !isOnboarding"
              class="edit_url"
              icon="icons/edit_pencil"
              @click="updateSitemapPageUrl(page)"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!isOnboarding && !isSubscribedToKit"
      class="sitemap_review"
    >
      <page-keywords-review-status
        :decision="page.review_status"
        :description="page.review_description || null"
        :keyword-count="page.keywords?.length"
      />
    </div>

    <div class="sitemap_assigned">
      <sitemap-row-tag
        v-for="(assignedKeyword, keyIndex) in page.keywords"
        :key="keyIndex"
        :page="page"
        :assigned-keyword="assignedKeyword"
        :is-onboarding="isOnboarding"
      />
      <primary-button
        v-if="isEditing || !isOnboarding"
        class="add_keyword"
        @click="openAddKeywordModal({page, pageIndex})"
      >
        + Add keyword
      </primary-button>
    </div>
    <div
      v-if="isEditing || !isOnboarding"
      class="sitemap_options"
      @mouseenter="showOptions = true"
      @mouseleave="showOptions = false"
    >
      <svg-vue icon="icons/dots-three-vertical" />
      <div
        v-if="showOptions"
        class="row_options"
      >
        <div
          @click="
            autoAssignKeywordsToPage(pageIndex);
            showOptions = false;
          "
        >
          <svg-vue icon="icons/star" />
          Auto-Assign
        </div>
        <div
          @click="
            refreshSitemapPage(page);
            showOptions = false;
          "
        >
          <svg-vue icon="icons/arrows-counter-clockwise" />
          Refresh
        </div>
        <div @click="handleShowRankingKeywordsClicked">
          <svg-vue icon="icons/bar-chart" />
          Ranking Keywords
        </div>
        <div
          class="delete_page"
          @click="
            removePageFromSitemap(page);
            showOptions = false;
          "
        >
          <svg-vue icon="icons/trash" />
          Delete
        </div>
      </div>
    </div>
    <ranking-keywords
      v-if="showRankingKeywordsModal"
      :page-url="page.url"
      @close="showRankingKeywordsModal = false;"
    />
  </div>
</template>

<script>
import PrimaryButton from "../../global/buttons/PrimaryButton";
import SitemapRowTag from "./SitemapRowTag";
import {mapActions, mapState} from "vuex";
import RankingKeywords from "../modals/RankingKeywords";
import PageKeywordsReviewStatus from "../includes/PageKeywordsReviewStatus.vue";
import useKit from "@/composables/useKit";

export default {
  components: {
    PageKeywordsReviewStatus,
    RankingKeywords,
    SitemapRowTag,
    PrimaryButton,
  },
  props: {
    pageIndex: {
      type: Number,
      required: true,
    },
    page: {
      type: Object,
      required: true,
    },
    pageCount: {
      type: Number,
      required: true,
    },
    disableReorder: {
      type: Boolean,
      required: true,
    },
    isOnboarding: {
      required: false,
      type: Boolean,
      default: false,
    }
  },
  setup() {
    const { isSubscribedToKit } = useKit();
    return { isSubscribedToKit };
  },
  data() {
    return {
      hover: false,
      showOptions: false,
      showRankingKeywordsModal: false,
    }
  },
  computed: {
    pageTitle() {
      if (this.page.refreshing) {
        return "We're fetching this info now...";
      }
      return this.page.pageTitle ?? "We couldn't find a page title.";
    },
    ...mapState('strategy', [
      'isEditing',
    ]),
  },
  mounted() {
    if (this.page.pageTitle === null) {
      this.refreshSitemapPage(this.page);
    }
  },
  methods: {
    ...mapActions('strategy', [
      'openAddKeywordModal',
      'reorderSitemapPage',
      'removePageFromSitemap',
      'autoAssignKeywordsToPage',
      'refreshSitemapPage',
      'updateSitemapPageUrl',
    ]),
    handleShowRankingKeywordsClicked() {
      this.showRankingKeywordsModal = true;

      window.mixpanel('Sitemap: View Page Ranking Keywords', {
        page_url: this.page.url,
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.sitemap_page_row {
  width: 100%;
  display: flex;
  border: 1px solid #E8E5E2;
  border-radius: 8px;
  padding: 16px 24px;

  &.review-bad {
    &:hover {
      background: #FDF7DB;
    }

    .ordering_buttons {
      .order {
        .move_up,
        .move_down {
          :deep(path) {
            stroke: #EAB64D;
          }
        }
      }
    }
  }

  &:not(:last-of-type) {
    margin-bottom: 1rem;
  }

  &:hover {
    background: #F9F6FF;
  }

  .sitemap_page {
    width: 40%;
    display: flex;
    align-items: center;

    .ordering_buttons {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      padding: 0 8px 0 0;
      gap: 4px;

      .order {
        width: 24px;
        height: 24px;
        cursor: pointer;

        &.disabled {
          .move_up, .move_down {
            :deep(path) {
              stroke: #E8E5E2;
            }
          }
        }

        .move_down {
          rotate: 180deg;
        }
      }
    }

    .sitemap_page_info {
      min-height: 52px;
      display: flex;
      flex-direction: column;
      justify-content: center;

      .page_title {
        font-size: 18px;
        font-weight: 500;
        line-height: 26px;
        letter-spacing: -0.5px;
        text-align: left;
        display: flex;
        align-items: center;
        gap: 10px;

        .refreshing {
          height: 16px;
          width: 16px;

          :deep(path) {
            stroke: var(--primary-color);
          }
        }

        .refresh_error {
          height: 16px;
          width: 16px;

          :deep(path) {
            stroke: var(--negative-color);
          }
        }
      }

      .page_url {
        font-size: 0.875rem;
        font-weight: 400;
        line-height: 1rem;
        letter-spacing: -0.1px;
        text-align: left;
        display: flex;
        align-items: center;

        .edit_url {
          margin-left: .5rem;
          cursor: pointer;
          height: 16px;
          width: 16px;
          left: 2px;
          top: 2px;
          border-radius: 0;
          padding: 3px; /* Increase clickable area */

          :deep(path) {
            stroke: #37B2A8;
          }
        }
      }
    }
  }

  .sitemap_review {
    width: 20%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .sitemap_assigned {
    width: 36%;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;

    .add_keyword {
      height: 24px;
      width: 110px;
      left: 305px;
      border-radius: 6px;
      padding: 4px 8px 4px 8px;
      background-color: var(--secondary-color);
      border-color: var(--secondary-color);
      transition: background-color .3s;

      &:hover {
        background-color: var(--secondary-color-dark);
        border-color: var(--secondary-color-dark);
      }

      :deep(.text) {
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
        letter-spacing: -0.5px;
        text-transform: capitalize;
        text-align: center;
      }
    }
  }

  .sitemap_options {
    flex: 1;
    align-self: center;
    position: relative;
    display: flex;
    justify-content: flex-end;

    svg {
      height: 24px;
      width: 24px;
    }

    .row_options {
      position: absolute;
      top: 0;
      right: 18px;
      background: #FFFFFF;
      border: 1px solid #F0E9FF;
      border-radius: 12px;
      padding: 16px;
      display: flex;
      flex-direction: column;
      white-space: nowrap;
      z-index: 10;

      div {
        padding: 8px 12px;
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
        letter-spacing: -0.1px;
        text-align: left;
        cursor: pointer;

        &:hover {
          background: #F0E9FF;
          border-radius: 8px;
        }

        svg {
          height: 18px;
          width: 18px;

          :deep(path) {
            stroke: #000000;
          }
        }

        &.delete_page {
          color: var(--negative-color);

          svg {
            :deep(path) {
              stroke: var(--negative-color);
            }
          }
        }
      }
    }
  }
}

.sitemap_page_row_onboarding {
  .sitemap_page {
    width: 100%;
    flex-grow: 1;
  }

  .sitemap_assigned {
    flex-shrink: 0;
  }

  .sitemap_options {
    flex-grow: 0;
  }
}
</style>
